import React, { useState } from 'react';
import { MapPin, Briefcase, Star, Users } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { useConnectionSuggestions, ConnectionSuggestion } from '@/features/home/hooks/useConnectionSuggestions';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { supabase } from '@/integrations/supabase/client';
import { getInitials } from '@/utils/avatarUtils';
import { useToast } from '@/components/ui/use-toast';
import { useI18n } from '@/contexts/I18nContext';
import { useNavigate } from 'react-router-dom';
import { SingleStepWizardModal, WizardStepType } from '@/components/sossial/SingleStepWizardModal';
import { EditLocationModal } from '@/features/profile/components/EditLocationModal';
import { useMySignals } from '@/features/sossial/hooks/useMySignals';
import { CtaCard } from '@/components/discovery/CtaCard';
import { DiscoverySection } from '@/components/discovery/DiscoverySection';
import { bucketSuggestions } from '@/features/sossial/utils/connectionSuggestionFilters';

// ─── User card (150×182, pixel-perfect from Figma 11482:682) ─────────────────
interface UserCardProps {
  suggestion: ConnectionSuggestion;
  onConnect: (id: string) => void;
  isPending: boolean;
  isSent: boolean;
  connectLabel: string;
  sentLabel: string;
}

const UserCard = ({ suggestion, onConnect, isPending, isSent, connectLabel, sentLabel }: UserCardProps) => {
  const navigate = useNavigate();
  const beltLabel = suggestion.belt_rank
    ? `${suggestion.belt_rank.charAt(0).toUpperCase()}${suggestion.belt_rank.slice(1)}`
    : 'White';
  const subtitle = [beltLabel, suggestion.academy_name].filter(Boolean).join(' · ');

  return (
    <div className="flex-shrink-0 w-[150px] bg-[#231a1a] border border-white/[0.06] rounded-[14px] overflow-hidden">
      {/* Avatar — centered, top padding 14px */}
      <button
        onClick={() => suggestion.username && navigate(`/profile/${suggestion.username}`)}
        className="w-full pt-[14px] flex justify-center"
      >
        <FramedAvatar
          src={suggestion.profile_picture_url}
          initials={getInitials(suggestion.full_name)}
          beltRank={suggestion.belt_rank || 'white'}
          size={28}
          frameSize={{ w: 48, h: 52 }}
          username={suggestion.username}
        />
      </button>

      {/* Name */}
      <p className="mt-[6px] px-[10px] text-[13px] font-semibold text-white text-center leading-[1.2] line-clamp-1">
        {suggestion.full_name}
      </p>

      {/* Belt · Academy */}
      <p className="mt-[2px] px-[10px] text-[10px] text-[#8d8d8d] text-center leading-[1.3] line-clamp-1">
        {subtitle}
      </p>

      {/* Connect button */}
      <div className="px-[10px] pb-[12px] mt-[14px]">
        <button
          onClick={() => !isSent && onConnect(suggestion.id)}
          disabled={isPending || isSent}
          className={`w-full h-[26.5px] rounded-full text-[12px] font-semibold transition-colors disabled:opacity-60 ${
            isSent ? 'bg-white/10 text-white/40' : 'bg-[#d72e16] text-white active:opacity-80'
          }`}
        >
          {isPending ? '···' : isSent ? sentLabel : connectLabel}
        </button>
      </div>
    </div>
  );
};


// ─── Main component ───────────────────────────────────────────────────────────
interface SearchSuggestionsProps {
  onSearch?: (query: string) => void;
  newSearchTerm?: string;
}

export const SearchSuggestions: React.FC<SearchSuggestionsProps> = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useI18n();
  const queryClient = useQueryClient();

  // Refresh key lets us re-trigger useMySignals after a modal save
  const [signalsRefreshKey, setSignalsRefreshKey] = useState(0);
  const { signals, loading: signalsLoading } = useMySignals(user?.id, signalsRefreshKey);
  const { data: suggestions, isLoading: suggestionsLoading } = useConnectionSuggestions();

  const [sentIds, setSentIds] = useState<Set<string>>(new Set());
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  // Modal state
  const [wizardType, setWizardType] = useState<WizardStepType | null>(null);
  const [locationOpen, setLocationOpen] = useState(false);

  const handleConnect = async (userId: string) => {
    if (!user) return;
    setPendingIds(prev => new Set(prev).add(userId));
    try {
      const { error } = await supabase
        .from('connections')
        .insert({ requester: user.id, recipient: userId, status: 'pending' });
      if (error) throw error;
      setSentIds(prev => new Set(prev).add(userId));
      toast({ title: t('connection.request_sent'), description: t('connection.request_sent_desc') });
    } catch {
      toast({ title: t('connection.request_error'), variant: 'destructive' });
    } finally {
      setPendingIds(prev => { const s = new Set(prev); s.delete(userId); return s; });
    }
  };

  // Called after any wizard save — refresh signals + connection suggestions
  const handleModalSaved = () => {
    setSignalsRefreshKey(k => k + 1);
    queryClient.invalidateQueries({ queryKey: ['connection-suggestions'] });
  };

  // Save location via EditLocationModal
  const handleLocationSave = async (data: { country: string; state: string; city: string }) => {
    if (!user) return;
    const { error } = await supabase
      .from('profiles')
      .update({ country: data.country, state: data.state, city: data.city })
      .eq('id', user.id);
    if (error) throw error;
    handleModalSaved();
    setLocationOpen(false);
  };

  const renderCard = (s: ConnectionSuggestion) => (
    <UserCard
      key={s.id}
      suggestion={s}
      onConnect={handleConnect}
      isPending={pendingIds.has(s.id)}
      isSent={sentIds.has(s.id)}
      connectLabel={t('discovery.connect')}
      sentLabel={t('discovery.sent')}
    />
  );

  if (!user) return null;

  if (signalsLoading || suggestionsLoading) {
    return (
      <div className="space-y-6 mt-2">
        {[1, 2, 3].map(i => (
          <div key={i}>
            <div className="h-4 w-36 bg-[#231a1a] rounded-full animate-pulse mb-3" />
            <div className="flex gap-[10px]">
              {[1, 2, 3].map(j => (
                <div key={j} className="flex-shrink-0 w-[150px] h-[182px] rounded-[14px] bg-[#231a1a] animate-pulse" />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const available = (suggestions ?? []).filter(s => !sentIds.has(s.id));

  const discoverySignals = signals
    ? {
        main_academy_id: signals.main_academy_id,
        profession: signals.profession,
        has_interests: signals.has_interests,
        hobby_ids: signals.hobby_ids ?? [],
        city: signals.city,
        state: signals.state,
        country: signals.country,
      }
    : null;

  const buckets = bucketSuggestions(available, discoverySignals);

  const emptyHint = (messageKey: string) => (
    <p className="text-[12px] text-[#8d8d8d] px-1">{t(messageKey)}</p>
  );

  return (
    <>
      <div className="space-y-6 mt-2">

        {/* ── People you might know — always first ── */}
        {buckets.peopleYouMightKnow.length > 0 && (
          <DiscoverySection title={t('discovery.people_you_might_know')}>
            {buckets.peopleYouMightKnow.map(renderCard)}
          </DiscoverySection>
        )}

        {/* ── Gym mates ── */}
        {!signals?.main_academy_id ? (
          <DiscoverySection title={t('discovery.gym_mates')} scroll={false}>
            <CtaCard
              title={t('discovery.cta.add_academy_title')}
              description={t('discovery.cta.add_academy_desc')}
              action={t('discovery.cta.add_now')}
              icon={<Users className="w-[18px] h-[18px]" />}
              onClick={() => setWizardType('academy')}
            />
          </DiscoverySection>
        ) : (
          <DiscoverySection
            title={t('discovery.gym_mates')}
            count={buckets.gymMates.length || undefined}
            seeAllHref={buckets.gymMates.length > 0 ? '/sossial/connections' : undefined}
            seeAllLabel={t('discovery.see_all')}
            scroll={buckets.gymMates.length > 0}
          >
            {buckets.gymMates.length > 0
              ? buckets.gymMates.map(renderCard)
              : emptyHint('discovery.no_gym_mates')}
          </DiscoverySection>
        )}

        {/* ── Same profession ── */}
        {!signals?.profession ? (
          <DiscoverySection title={t('discovery.same_profession')} scroll={false}>
            <CtaCard
              title={t('discovery.cta.add_profession_title')}
              description={t('discovery.cta.add_profession_desc')}
              action={t('discovery.cta.add_now')}
              icon={<Briefcase className="w-[18px] h-[18px]" />}
              onClick={() => setWizardType('profession')}
            />
          </DiscoverySection>
        ) : (
          <DiscoverySection title={t('discovery.same_profession')} scroll={buckets.sameProfession.length > 0}>
            {buckets.sameProfession.length > 0
              ? buckets.sameProfession.map(renderCard)
              : emptyHint('discovery.no_profession_matches')}
          </DiscoverySection>
        )}

        {/* ── Mutuals ── */}
        <DiscoverySection title={t('discovery.mutuals')} scroll={buckets.mutuals.length > 0}>
          {buckets.mutuals.length > 0
            ? buckets.mutuals.map(renderCard)
            : emptyHint('discovery.no_mutual_matches')}
        </DiscoverySection>

        {/* ── Same interests ── */}
        {!signals?.has_interests ? (
          <DiscoverySection title={t('discovery.same_interests')} scroll={false}>
            <CtaCard
              title={t('discovery.cta.pick_interests_title')}
              description={t('discovery.cta.pick_interests_desc')}
              action={t('discovery.cta.pick_now')}
              icon={<Star className="w-[18px] h-[18px]" />}
              onClick={() => setWizardType('interests')}
            />
          </DiscoverySection>
        ) : (
          <DiscoverySection title={t('discovery.same_interests')} scroll={buckets.sameInterests.length > 0}>
            {buckets.sameInterests.length > 0
              ? buckets.sameInterests.map(renderCard)
              : emptyHint('discovery.no_interest_matches')}
          </DiscoverySection>
        )}

        {/* ── Near to you ── */}
        {!signals?.country ? (
          <DiscoverySection title={t('discovery.near_to_you')} scroll={false}>
            <CtaCard
              title={t('discovery.cta.add_location_title')}
              description={t('discovery.cta.add_location_desc')}
              action={t('discovery.cta.add_now')}
              icon={<MapPin className="w-[18px] h-[18px]" />}
              onClick={() => setLocationOpen(true)}
            />
          </DiscoverySection>
        ) : (
          <DiscoverySection title={t('discovery.near_to_you')} scroll={buckets.nearYou.length > 0}>
            {buckets.nearYou.length > 0
              ? buckets.nearYou.map(renderCard)
              : emptyHint('discovery.no_location_matches')}
          </DiscoverySection>
        )}

      </div>

      {/* ── Modals ── */}
      <SingleStepWizardModal
        open={wizardType !== null}
        type={wizardType}
        onClose={() => setWizardType(null)}
        onSaved={handleModalSaved}
      />

      <EditLocationModal
        open={locationOpen}
        onClose={() => setLocationOpen(false)}
        onSave={handleLocationSave}
        initialData={{ country: '', state: '', city: '' }}
      />
    </>
  );
};
